import api from "./api";
import axios from "axios";
import { DestinationDto } from "../interfaces/Dtos";
import { TaxIdentificationTypeInterface } from "../interfaces/TaxIdentificationTypeInterface";
import {
  Nullable,
  AccountInterface,
  ResponseInterface,
  DestinationInterface,
  WhiteLocationInterface,
  AccountRequestInterface,
  BUSellerConfig,
  RecurrentDestinationInterface,
} from "../interfaces";
import { sanitizeFulltextQuery } from "../utils/searchText";
import { AccountBlocked } from "../components/Account/AccountBlocked";
import { AccountSiteBUDTO } from "../interfaces/Dtos/AccountSiteBUDTO";
import { AccountSiteLockDTO } from "../interfaces/Dtos/AccountSiteLockDTO";

const baseURLAPI = process.env.REACT_APP_API_HOST;

export const getAccount = async (
  accountId: string
): Promise<Nullable<AccountInterface>> => {
  if (!accountId) {
    return null;
  }
  const uri = `${baseURLAPI}/Account/GetAccount?accountId=${accountId}`;

  try {
    const response = await api.get(uri);

    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {}

  return null;
};

export const getAccountByIdentification = async (
  abreviationName: string,
  identificationNumber: string
): Promise<ResponseInterface<AccountInterface>> => {
  if (!abreviationName || !identificationNumber) {
    return {
      model: null,
      didError: true,
      message: "Invalid parameters",
      errorMessage: "Invalid parameters",
    };
  }
  const uri = `${baseURLAPI}/GetAccount?abreviationName=${abreviationName}&identificationNumber=${identificationNumber}`;

  try {
    const response = await api.get(uri);

    return response.data;
  } catch (error) {
    const response = (error as any)?.response;

    if (response) {
      return response.data;
    }

    return {
      model: null,
      didError: true,
      message: `Error: ${error}`,
      errorMessage: "Application error 1",
    };
  }
};

export const saveAccount = async (
  account: AccountRequestInterface
): Promise<ResponseInterface<AccountInterface>> => {
  account.creationDate = new Date().toISOString();
  const body = JSON.stringify(account);

  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();
  const uri = `${baseURLAPI}/Account`;

  try {
    const response = await api.post(uri, body, {
      headers: {
        "Content-Type": "application/json",
      },
      cancelToken: source.token,
    });

    if (response.status === 200) {
      return response.data;
    }
    return {
      model: null,
      didError: true,
      message: `Server status: ${response.status}`,
      errorMessage: "Server error",
    };
  } catch (error) {
    return {
      model: null,
      didError: true,
      message: `Error: ${error}`,
      errorMessage: "Application error 2",
    };
  }
};

export const updateAccount = async (
  account: AccountRequestInterface
): Promise<ResponseInterface<AccountInterface>> => {
  const body = JSON.stringify(account);

  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();
  const uri = `${baseURLAPI}/Account`;

  try {
    const response = await api.put(uri, body, {
      headers: {
        "Content-Type": "application/json",
      },
      cancelToken: source.token,
    });

    if (response.status === 200) {
      return response.data;
    }
    return {
      model: null,
      didError: true,
      message: `Server status: ${response.status}`,
      errorMessage: "Server error",
    };
  } catch (error) {
    return {
      model: null,
      didError: true,
      message: `Error: ${error}`,
      errorMessage: "Application error 3",
    };
  }
};

const FILTERED_ACCOUNTS_TIMEOUT = 15000;

export const getFilteredAccounts = async (
  criteria: string
): Promise<ResponseInterface<AccountInterface[]>> => {
  const uri = `${baseURLAPI}/Account?idStr=${criteria}&maxRows=20&withAgreementAuths=true`;
  try {
    // Same bound as the consignee book: the Clientes group sets its spinner
    // on the keystroke, and an unbounded request leaves it spinning forever.
    const response = await api.get(uri, { timeout: FILTERED_ACCOUNTS_TIMEOUT });

    if (response.status === 200) {
      return {
        model: response.data,
        didError: false,
        message: "",
        errorMessage: "",
      };
    }
  } catch (error) {
    return (error as any)?.response?.data;
  }

  return {
    model: null,
    didError: true,
    message: "Hubo un error en la búsqueda de clientes.",
    errorMessage: "Hubo un error en la búsqueda de clientes.",
  };
};

/**
 * Sales window we ask for. The procedure's own default is already 30
 * (`@LastSalesDays INT = 30`, verified 2026-09-12); it is the .NET layer that
 * defaults to 90, so sending this explicitly is what keeps the window narrow.
 */
export const DEFAULT_LAST_SALES_DAYS = 30;

/**
 * The only two values the API accepts. Anything else is a 400 with
 * "LastSalesDays debe ser 30 o 90" (measured against dev 2026-09-13), so the
 * type keeps a caller from discovering that at runtime.
 */
export type LastSalesDays = 30 | 90;

const FAVORITE_DESTINATIONS_TTL = 5 * 60 * 1000;
const FAVORITE_DESTINATIONS_TIMEOUT = 15000;

interface FavoriteDestinationsCacheEntry {
  expiresAt: number;
  promise: Promise<ResponseInterface<RecurrentDestinationInterface[]>>;
}

/**
 * The consignee book, keyed by shipper and held in memory only.
 *
 * No Redux and no storage: redux-persist wraps the whole root reducer with no
 * blacklist (store.tsx), so a slice would write consignee PII to localStorage.
 * A module-level Map dies with the page and is cleared at logout.
 */
const favoriteDestinationsCache = new Map<
  string,
  FavoriteDestinationsCacheEntry
>();

const favoriteDestinationsError =
  (): ResponseInterface<RecurrentDestinationInterface[]> => ({
    model: null,
    didError: true,
    message: "Hubo un error obteniendo el historial de búsquedas.",
    errorMessage: "Hubo un error obteniendo el historial de búsquedas.",
  });

/**
 * A rejection only carries a usable payload when the server answered with our
 * own envelope. A network error, a timeout (ECONNABORTED) or a failed token
 * refresh reject with something else entirely, and returning that raw value
 * would make every caller throw on `response.didError`.
 */
const isResponseShape = (
  data: any
): data is ResponseInterface<RecurrentDestinationInterface[]> =>
  !!data && typeof data === "object" && typeof data.didError === "boolean";

/**
 * Drop cached books. With no argument it clears every shipper — call it at
 * logout, since tellers share a PC and the book is customer PII.
 */
export const invalidateFavoriteDestinationsCache = (shipperId?: string) => {
  if (shipperId === undefined) {
    favoriteDestinationsCache.clear();
    return;
  }

  favoriteDestinationsCache.delete(shipperId);
};

const requestFavoriteAndHistoricalLocations = async (
  accountId: string,
  sanitizedSearch: string,
  lastSalesDays: LastSalesDays
): Promise<ResponseInterface<RecurrentDestinationInterface[]>> => {
  const uri =
    `${baseURLAPI}/GetFavoriteAndRecentDestinations` +
    `?AccountID=${encodeURIComponent(accountId)}` +
    `&SearchInput=${encodeURIComponent(sanitizedSearch)}` +
    `&LastSalesDays=${lastSalesDays}`;

  try {
    // The stored procedure is what blocks production, so bound the wait: with
    // no timeout a hung request leaves the dropdown spinning indefinitely.
    const response = await api.get(uri, {
      timeout: FAVORITE_DESTINATIONS_TIMEOUT,
    });

    if (
      response.status === 200 &&
      !response.data.didError &&
      !!response.data.model
    ) {
      return {
        message: "",
        didError: false,
        errorMessage: "",
        model: response.data.model,
      };
    } else if (response.status === 200 && isResponseShape(response.data)) {
      return response.data;
    }
  } catch (error) {
    const data = (error as any)?.response?.data;
    if (isResponseShape(data)) {
      return data;
    }
  }

  return favoriteDestinationsError();
};

export const getFavoriteAndHistoricalLocations = async (
  accountId: string,
  search: string = "",
  lastSalesDays: LastSalesDays = DEFAULT_LAST_SALES_DAYS
): Promise<ResponseInterface<RecurrentDestinationInterface[]>> => {
  const sanitizedSearch = sanitizeFulltextQuery(search);

  // Only the empty search — the shipper's whole book — is cached. A filtered
  // search is a different, uncapped query and is always issued.
  if (sanitizedSearch !== "" || lastSalesDays !== DEFAULT_LAST_SALES_DAYS) {
    return requestFavoriteAndHistoricalLocations(
      accountId,
      sanitizedSearch,
      lastSalesDays
    );
  }

  const now = Date.now();
  const cached = favoriteDestinationsCache.get(accountId);
  if (!!cached && cached.expiresAt > now) {
    return cached.promise;
  }

  // Sweep on insert: expiry is otherwise only checked on read, so a teller
  // moving through many shippers would keep every book resident all day.
  favoriteDestinationsCache.forEach((entry, key) => {
    if (entry.expiresAt <= now) {
      favoriteDestinationsCache.delete(key);
    }
  });

  const entry: FavoriteDestinationsCacheEntry = {
    expiresAt: now + FAVORITE_DESTINATIONS_TTL,
    // The stored promise must never reject: a lote mounts one search box per
    // guía and they all await this same promise.
    promise: requestFavoriteAndHistoricalLocations(
      accountId,
      sanitizedSearch,
      lastSalesDays
    )
      .then((response) =>
        !!response && !response.didError && !!response.model
          ? response
          : favoriteDestinationsError()
      )
      .catch(() => favoriteDestinationsError()),
  };

  // Stored synchronously, before the first await, so every box mounted in the
  // same commit shares one request instead of firing one each.
  favoriteDestinationsCache.set(accountId, entry);

  entry.promise.then((response) => {
    // Compare identity before evicting: an invalidation plus a fresh fetch can
    // happen while this request is still in flight, and its late failure must
    // not throw away the newer entry.
    if (
      response.didError &&
      favoriteDestinationsCache.get(accountId) === entry
    ) {
      favoriteDestinationsCache.delete(accountId);
    }
  });

  return entry.promise;
};

export const getAccountWhitelist = async (
  taxIdentificationTypes: TaxIdentificationTypeInterface[],
  accountId?: string,
  agreementID?: string,
  isLocked?: boolean
): Promise<ResponseInterface<WhiteLocationInterface>> => {
  const uri = `${baseURLAPI}/Agreement/Location?`;
  let first = true;
  if (accountId) {
    uri.concat(`AccountID=${accountId}`);
    first = false;
  }
  if (agreementID) {
    if (first) {
      uri.concat(`AgreementID=${agreementID}`);
    } else {
      uri.concat(`&AgreementID=${agreementID}`);
    }
    first = false;
  }
  if (isLocked) {
    if (first) {
      uri.concat(`IsLocked=${isLocked}`);
    } else {
      uri.concat(`&IsLocked=${isLocked}`);
    }
  }

  try {
    const response = await api.get(uri);

    if (
      response.status === 200 &&
      !response.data.didError &&
      !!response.data.model
    ) {
      const whiteLocations = response.data.model;

      // Map the objet to a AgreementLocationsListInterface
      return {
        message: "",
        didError: false,
        errorMessage: "",
        model: {
          accountID: whiteLocations.accountID,
          consigneeDestinationList: whiteLocations.consigneeDestinationList.map(
            (destination: DestinationDto) =>
              mapToDestinationInterface(destination, taxIdentificationTypes)
          ),
        },
      };
    } else if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return (error as any)?.response?.data;
  }

  return {
    model: null,
    didError: true,
    message: "Hubo un error obteniendo las cuentas permitidas.",
    errorMessage: "Hubo un error obteniendo las cuentas permitidas.",
  };
};

// Method to map DestinationDto to DestinationInterface
export const mapToDestinationInterface = (
  destination: DestinationDto,
  taxIdTypes: TaxIdentificationTypeInterface[]
): DestinationInterface => {
  const taxIdType = taxIdTypes.find(
    (taxIdType) =>
      taxIdType.taxIdentificationTypeId === destination.taxIdentificationTypeID
  );
  return {
    client: {
      id: destination.accountID,
      accountCode: destination.accountCode,
      taxIdentificationTypeID: destination.taxIdentificationTypeID,
      identificationNumber: destination.identificationNumber,
      accountFullName: destination.accountFullName,
      taxIdentificationTypeCode: taxIdType?.taxIdentificationTypeCode ?? "",
      abreviationName: taxIdType?.abreviationName ?? "",
      listAccountPhone: destination.accountPhoneList.map((phone) => ({
        phoneID: phone.accountPhoneID,
        accountID: destination.accountID,
        countryID: phone.accountPhoneCountryID,
        phoneNumber: phone.accountPhoneNumber,
        countryPhoneAccessCode: phone.accountCountryPhoneAccessCode,
        phoneTypeID: phone.accountPhoneTypeID,
      })),
      listAccountEmail: [],
      listAuthorizingAccount: [],
      creationDate: "",
      creationUser: "",
    },
    location: {
      name: destination.addressName,
      address:
        destination.addressLine1 +
        (destination.addressLine2 !== null
          ? " " + destination.addressLine2
          : ""),
      reference: destination.addressLandMark,
      postalCode: destination.postalCode,
      coordinates: {
        lat: destination.latitude ?? 0,
        lng: destination.longitude ?? 0,
      },
    },
    businessUnit: {
      id: destination.businessUnit.buid.toString(),
      name: destination.businessUnit.buName,
      code: destination.businessUnit.buCode,
      location: {
        name: destination.businessUnit.buAreaName ?? "",
        address: destination.businessUnit.address ?? "",
        reference: destination.businessUnit.reference ?? "",
        postalCode: destination.businessUnit.postalCode ?? "",
        coordinates: {
          lat: !!destination.businessUnit.latitude
            ? parseFloat(destination.businessUnit.latitude)
            : 0,
          lng: !!destination.businessUnit.longitude
            ? parseFloat(destination.businessUnit.longitude)
            : 0,
        },
      },
      polygonList: [],
    },
    homeDelivery: destination.isHomeDelivery,
  };
};

export const verifyAccountBlocked = async (
  accountID: string,
  serviceID: number,
  paymentModeID: number,
  buCode: string
): Promise<ResponseInterface<AccountBlocked[]>> => {
  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();
  const uri = `${baseURLAPI}/Account/VerifyAccountBlocked?AccountID=${accountID}&ServiceID=${serviceID}&PaymentModeID=${paymentModeID}&BUCode=${buCode}`;

  try {
    const response = await api.get(uri, {
      headers: {
        "Content-Type": "application/json",
      },
      cancelToken: source.token,
    });

    if (response.status === 200) {
      return response.data;
    }
    return {
      model: null,
      didError: true,
      message: "",
      errorMessage: "Hubo un error al verificar la lista negra de los cupones",
    };
  } catch (error) {
    return (error as any)?.response?.data;
  }
};

export const reserveBUSeller = async (
  buSeller: string,
  accountID: string,
  user: string
): Promise<ResponseInterface<BUSellerConfig>> => {
  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();
  const uri = `${baseURLAPI}/Account/ReserveBUSeller`;

  const body = JSON.stringify({
    BUSeller: buSeller,
    AccountID: accountID,
    User: user,
  });

  try {
    const response = await api.post(uri, body, {
      headers: {
        "Content-Type": "application/json",
      },
      cancelToken: source.token,
    });

    return response.data;
  } catch (error) {}

  return {
    model: null,
    didError: true,
    message: "",
    errorMessage: "",
  };
};

export const getAccountSiteBUByAccountSiteID = async (
  accountSiteID: string
): Promise<ResponseInterface<AccountSiteBUDTO[]>> => {
  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();
  const uri = `${baseURLAPI}/Account/GetAccountSiteBUByAccountSiteID?AccountSiteID=${accountSiteID}`;

  try {
    const response = await api.get(uri, {
      headers: {
        "Content-Type": "application/json",
      },
      cancelToken: source.token,
    });
    
    return response.data;
  } catch (error) {}
    return {
      model: null,
      didError: true,
      message: "",
      errorMessage: "",
    };
}

export const GetAccountSiteLockByAccountSiteID = async (
  accountSiteID: string
): Promise<ResponseInterface<AccountSiteLockDTO[]>> => {
  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();
  const uri = `${baseURLAPI}/Account/GetAccountSiteLockByAccountSiteID?AccountSiteID=${accountSiteID}`;

  try {
    const response = await api.get(uri, {
      headers: {
        "Content-Type": "application/json",
      },
      cancelToken: source.token,
    });
    
    return response.data;
  } catch (error) {}
    return {
      model: null,
      didError: true,
      message: "",
      errorMessage: "",
    };
}

